<script>
    import Button from "$lib/atoms/Button.svelte";
    import { goto } from "$app/navigation";
    import { page } from "$app/stores";
    import { ArrowRight } from "lucide-svelte";
    import { onMount } from 'svelte';
    import { browser } from '$app/environment';
    import { getConfig } from '$lib/config/environment.js';
    import { apiFetch } from '$lib/services/api-fetch.js';
    import { t } from '$lib/i18n.js';

    let days = 15;
    let selectedCarType = '';
    let selectedCarYear = '';
    let currency = "€";
    let earnings = 0;

    // Dati dall'API
    let carTypes = [];
    let carYears = [];
    let feeData = null;
    let isLoading = true;
    let error = null;

    const config = getConfig();

    // Calcolo dinamico del valore
    $: if (browser && (selectedCarType || selectedCarYear || days || feeData)) {
        // console.debug('Reactive statement triggered for earnings calculation');
        earnings = calculateEarnings();
    }
    
    // Debug reattivo
    $: if (browser) {
        /*
        console.debug('Debug earnings calculation:', {
            selectedCarType,
            selectedCarYear,
            days,
            carTypes: carTypes.length,
            carYears: carYears.length,
            feeData,
            earnings
        });
        */
    }

    const AUTH_HEADER = 'JHC-TOKEN username="jhc", sessionid="ahtS1!6&009qEjss"';

    async function fetchAPI(url) {
        const response = await apiFetch(url, {
            headers: {
                'Authorization': AUTH_HEADER,
                'Content-Type': 'application/json'
            }
        });
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        return response.json();
    }

    async function loadCarTypes() {
        try {
            const data = await fetchAPI(`${config.api.baseUrl}/list/getCarTypes`);
            //console.debug('Car types loaded:', data);
            carTypes = data;
            if (carTypes.length > 0) {
                selectedCarType = carTypes[0].categoria;
                //console.debug('Auto-selected car type:', selectedCarType);
            }
        } catch (err) {
            console.error('Error loading car types:', err);
            error = 'Failed to load car types';
        }
    }

    async function loadCarYears() {
        try {
            const data = await fetchAPI(`${config.api.baseUrl}/list/getYears`);
            //console.debug('Car years loaded:', data);
            carYears = data;
            if (carYears.length > 0) {
                selectedCarYear = carYears[0].year;
                //console.debug('Auto-selected car year:', selectedCarYear);
            }
        } catch (err) {
            console.error('Error loading car years:', err);
            error = 'Failed to load car years';
        }
    }

    async function loadFeeData() {
//         console.debug('=== LOAD FEE DATA CALLED ===');
        // console.debug('selectedCarType:', selectedCarType);
        // console.debug('selectedCarYear:', selectedCarYear);
        
        if (!selectedCarType || !selectedCarYear) {
            console.warn('Exiting loadFeeData - missing selections');
            return;
        }
        
        try {
            const url = `${config.api.baseUrl}/pricing/getFee/?anno=${selectedCarYear}&categoria=${selectedCarType}`;
            // console.debug('Loading fee data from:', url);
            const data = await fetchAPI(url);
            // console.debug('Fee data loaded successfully:', data);
            feeData = data;
            // console.debug('feeData assigned, triggering earnings recalculation...');
            
            // Forza il ricalcolo
            earnings = calculateEarnings();
            // console.debug('Forced earnings recalculation result:', earnings);
        } catch (err) {
            console.error('Error loading fee data:', err);
            error = 'Failed to load fee data';
        }
    }

    function calculateEarnings() {
        // console.debug('=== CALCULATE EARNINGS DEBUG ===');
        // console.debug('selectedCarType:', selectedCarType, typeof selectedCarType);
        // console.debug('selectedCarYear:', selectedCarYear, typeof selectedCarYear);
        // console.debug('carTypes.length:', carTypes.length);
        // console.debug('carYears.length:', carYears.length);
        // console.debug('feeData:', feeData);
        // console.debug('days:', days);
        
        if (!selectedCarType || !selectedCarYear || selectedCarType === '' || selectedCarYear === '' || !carTypes.length || !carYears.length || !feeData) {
            console.warn('Insufficient data for earnings calculation - returning 0');
            return 0;
        }

        // Trova i dati per la categoria selezionata
        const carTypeData = carTypes.find(ct => ct.categoria === selectedCarType);
        const yearData = carYears.find(cy => cy.year == selectedCarYear); // Usa == per conversione automatica
        
        // console.debug('carTypeData found:', carTypeData);
        // console.debug('yearData found:', yearData);
        
        if (!carTypeData || !yearData || !feeData) {
            console.warn('Missing data - carTypeData:', carTypeData, 'yearData:', yearData, 'feeData:', feeData);
            return 0;
        }

        // Formula: valore di categoria * deprezzamento * (1 - fee_owner) * giorni
        const categoryValue = carTypeData.valore;
        const depreciation = yearData.percentuale;
        const feeOwner = feeData.fee_owner;

        // console.debug('Formula components:');
        // console.debug('categoryValue:', categoryValue);
        // console.debug('depreciation:', depreciation);
        // console.debug('feeOwner:', feeOwner);
        // console.debug('days:', days);
        
        const result = Math.round(categoryValue * depreciation * (1 - feeOwner) * days);
        // console.debug('Final calculation result:', result);
        // console.debug('=== END DEBUG ===');
        
        return result;
    }

    // Reagisce ai cambiamenti di selezione
    $: if (browser && selectedCarType && selectedCarYear && selectedCarType !== '' && selectedCarYear !== '') {
        loadFeeData();
    }

    onMount(async () => {
        try {
            const { featureFlags } = await import('$lib/stores/feature-flags.js');
            const start = featureFlags.get('starting-simulator-value');
            if (start != null) days = +start || days;
        } catch {}
        if (browser) {
            try {
                await Promise.all([loadCarTypes(), loadCarYears()]);
                
                // Carica i dati delle fee una volta che entrambe le selezioni sono impostate
                if (selectedCarType && selectedCarYear && selectedCarType !== '' && selectedCarYear !== '') {
                    // console.debug('Loading fee data with auto-selected values:', selectedCarType, selectedCarYear);
                    await loadFeeData();
                }
                
                isLoading = false;
            } catch (err) {
                console.error('Error during initialization:', err);
                error = 'Failed to initialize data';
                isLoading = false;
            }
        }
    });

    function handlePublish() {
        goto(`/publishCar/setCarInfo`);
    }
</script>

<section class="earn-section">
    <div class="earn-form">
        <div class="col mobile-only">
            <img src="https://assets.auting.it/images/photos/woman-car.webp" alt="{$t('routes.owner.earn.img_alt')}" />
        </div>
        <div class="col">
            <div class="max-width">

{#if isLoading}
            <h1 class="headline">{$t('routes.owner.earn.loading')}</h1>
            <div class="loading-spinner">
                <p>{$t('routes.owner.earn.loading_car_data')}</p>
            </div>
        {:else if error}
            <h1 class="headline">{$t('routes.owner.earn.loading_error')}</h1>
            <div class="error-message">
                <p>{error}</p>
            </div>
        {:else}
            <h1 class="headline">{$t('routes.owner.earn.title').replace('{currency}', currency).replace('{earnings}', earnings).replace('{days}', days).replace('{unit}', days === 1 ? $t('words.day') : $t('words.days'))}</h1>
            <!-- Slider per giorni -->
            <div class="form-group">
                <div class="slider-container">
                    <input
                        type="range"
                        id="days-slider"
                        bind:value={days}
                        min="1"
                        max="30"
                        step="1"
                        class="slider"
                    />
                    <div
                        class="slider-bubble"
                        style="left: {((days - 1) / 29) * 100}%;"
                    >
                        {$t('routes.owner.earn.slider').replace('{days}', days).replace('{unit}', days === 1 ? $t('words.day') : $t('words.days'))}
                    </div>
                </div>
            </div>

            <!-- Dropdown per tipo auto e anno -->
            <div class="form-group">
                <div class="row">
                <select id="car-type" bind:value={selectedCarType} class="dropdown" disabled={isLoading}>
                    <option value="" disabled>{$t('routes.owner.earn.select_car_type') || 'Seleziona tipo auto'}</option>
                    {#each carTypes as type}
                        <option value={type.categoria}>{$t('routes.car.categories.' + type.categoria)}</option>
                    {/each}
                </select>
                <select id="car-year" bind:value={selectedCarYear} class="dropdown" disabled={isLoading}>
                    <option value="" disabled>{$t('routes.owner.earn.select_car_year') || 'Seleziona anno'}</option>
                    {#each carYears as year}
                        <option value={year.year}>{year.year}</option>
                    {/each}
                </select>
            </div>
        </div>
        
        <!-- Bottone publish -->
        <div class="form-group btn-group">
            {#if selectedCarType && selectedCarYear && selectedCarType !== '' && selectedCarYear !== '' && feeData}
                <p>{$t('routes.owner.earn.estimated_earnings').replace('{currency}', currency).replace('{earnings}', earnings).replace('{days}', days).replace('{unit}', days === 1 ? $t('words.day') : $t('words.days'))}</p>
            {/if}
            <Button
                icon={ArrowRight}
                label="{$t('routes.owner.earn.cta')}"
                backgroundColor="var(--water-green)"
                textColor="var(--on-primary)"
                onclick={handlePublish}
                size="large"
            />
        </div>
        {/if}
        </div>
        </div>
        <div class="col desktop-only">
            <img src="https://assets.auting.it/images/photos/woman-car.webp" alt="{$t('routes.owner.earn.cta')}" class="image"/>
        </div>
    </div>
</section>
<img src="https://assets.auting.it/images/waves/earn/wave-bottom.svg" alt="Wave" class="wave-bottom-header" />

<style>
    section.earn-section {
        padding: 2rem;
        background: var(--surface-container);
        min-height: 80vh;
        display: flex;
        align-items: center;
        justify-content: center;
        padding-top: 120px;
    }

    .col {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
flex: 1;
    }

    .col img {
        width: 100%;
        aspect-ratio: 1;
        background: var(--surface-container);
        border-radius: 24px;
        object-fit: cover;
        object-position: right;
    }

    .max-width {
        max-width: 500px;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: center;
        height: 100%;
    }

    .earn-form {
        background: white;
        padding: 16px;
        border-radius: 32px;
        margin-bottom: 2rem;
        display: flex;
        flex-direction: row;
        gap: 40px;
        width: 100%;
        max-width: var(--max-width);
    }

    .form-group {
        margin-bottom: 2rem;
        text-align: left;
    }

    .form-group.btn-group {
        display: flex;
        justify-content: center;
        flex-direction: column;
        align-items: center;
        gap: 0px;
    }

    .form-label {
        display: block;
        font-size: 1.1rem;
        font-weight: 600;
        color: var(--color-theme-2);
        margin-bottom: 0.75rem;
    }

    .days-value {
        color: var(--water-green);
        font-weight: 700;
    }

    .row {
        display: flex;
        flex-direction: row;
        gap: 16px;
    }

    /* Slider Styling */
    .slider-container {
        position: relative;
        margin-top: 1rem;
    }

    .headline {
        font-size: 48px;
        text-align: center;
        color: var(--on-surface);
        margin-bottom: 16px;
        font-family: var(--font-body);
         margin-bottom: 56px;
        line-height: 1.2;
        letter-spacing: -2.5px;
    }

    .slider {
        -webkit-appearance: none;
        width: 100%;
        height: 8px;
        border-radius: 5px;
        background: #e0e0e0;
        outline: none;
        cursor: pointer;
    }

    .slider::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: var(--water-green);
        cursor: pointer;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        transition: all 0.2s ease;
    }

    .slider::-webkit-slider-thumb:hover {
        transform: scale(1.1);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    }

    .slider::-moz-range-thumb {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: var(--water-green);
        cursor: pointer;
        border: none;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }

    .slider-bubble {
        position: absolute;
        top: -50px;
        transform: translateX(-50%);
        background: var(--on-surface);
        color: white;
        padding: 8px 12px;
        border-radius: 20px;
        font-size: 0.9rem;
        font-weight: 600;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        transition: left 0.1s ease;
    }

    .slider-bubble::after {
        content: "";
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border: 8px solid transparent;
        border-top-color: var(--on-surface);
    }

    /* Dropdown Styling */
    .dropdown {
        width: 100%;
        padding-left: 8px;
        padding-top: 16px;
        padding-bottom: 16px;
        padding-right: 8px;
        border: 2px solid #e0e0e0;
        border-radius: 12px;
        font-size: 1rem;
        background: white;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .dropdown:focus {
        outline: none;
        border-color: var(--water-green);
        box-shadow: 0 0 0 3px rgba(74, 222, 128, 0.1);
    }

    .dropdown:hover {
        border-color: var(--water-green);
    }

    .dropdown:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    /* Loading and Error States */
    .loading-spinner {
        text-align: center;
        padding: 2rem;
        color: var(--on-surface);
    }

    .error-message {
        text-align: center;
        padding: 2rem;
        color: #e74c3c;
        background: #ffeaea;
        border-radius: 12px;
        margin: 1rem 0;
    }

    /* Publish Button */
    .publish-button-container {
        display: flex;
        justify-content: center;
        margin-top: 2rem;
    }

    .wave-bottom-header {
        width: 100vw;
        margin-top: -1px;
    }

    .mobile-only {
        display: none;
    }
 
    /* Mobile Responsiveness */
    @media (max-width: 768px) {
        .earn-section {
            padding: 2rem 1rem;
            padding-top: 100px;
        }

        .earn-header h1 {
            font-size: 2rem;
        }

        .earn-form {
            flex-direction: column;
            gap: 40px;
            padding-bottom: 24px;
        }

        .headline {
            font-size: 40px;
            letter-spacing: -2px;
        }

        .row {
            flex-direction: column;
            align-items: stretch;
        }

        .slider-bubble {
            top: -45px;
            padding: 6px 10px;
            font-size: 0.8rem;
        }

        .mobile-only {
            display: block;
        }

        .desktop-only {
            display: none;
        }
    }
</style>
